import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import DeepLinkPage from '../DeepLinkPage';
import { buildScheme } from '../DeepLinkUtils';
import { supabase } from '../../lib/supabase';

// Helper to decode JWT payload and extract company_id
function getJwtPayload(token: string): { company_id?: string; draft_student_id?: string } {
  try {
    const [, payloadBase64] = token.split('.');
    if (!payloadBase64) return {};
    const payload = JSON.parse(atob(payloadBase64));
    return {
      company_id: payload?.user_metadata?.company_id || payload?.company_id,
      draft_student_id: payload?.user_metadata?.draft_student_id,
    };
  } catch {
    return {};
  }
}

export default function DraftInviteAuthRedirect() {
  const navigate = useNavigate();
  const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
  const hp = useMemo(() => new URLSearchParams(hash), [hash]);
  const sp = useMemo(() => new URLSearchParams(window.location.search), []);

  const accessToken = hp.get('access_token') || sp.get('access_token') || '';
  const refreshToken = hp.get('refresh_token') || sp.get('refresh_token') || '';
  const tokenType = hp.get('token_type') || sp.get('token_type') || '';
  const rawType = (hp.get('type') || sp.get('type') || '').toLowerCase();
  const normalizedType = rawType === 'invite' || rawType === 'signup' ? 'invite' : rawType;
  
  // CRITICAL FIX: Handle token exchange for magic links/invite links
  const tokenParam = sp.get('token'); // Token from Supabase auth URL
  const typeParam = sp.get('type') || rawType; // Type from URL
  
  const [processing, setProcessing] = useState(false);
  const [showContinueWeb, setShowContinueWeb] = useState(false);
  const [_exchangingToken, setExchangingToken] = useState(!!tokenParam && !accessToken);
  
  // Check for error parameters
  const error = hp.get('error') || sp.get('error') || '';
  const errorDescription = hp.get('error_description') || sp.get('error_description') || '';
  const errorCode = hp.get('error_code') || sp.get('error_code') || '';

  // CRITICAL FIX: If we have a token but no access_token, exchange it OR redirect to code entry
  useEffect(() => {
    if (tokenParam && !accessToken) {
      // Extract email from token if possible, or redirect to code entry with token
      // For now, redirect to code entry page - user will enter code from email
      const email = sp.get('email');
      if (email) {
        navigate(`/enter-invite-code?email=${encodeURIComponent(email)}`);
      } else {
        // Try to exchange token
        const exchangeToken = async () => {
          setExchangingToken(true);
          try {
            const { data, error: exchangeError } = await supabase.auth.verifyOtp({
              token_hash: tokenParam,
              type: typeParam === 'recovery' ? 'recovery' : typeParam === 'magiclink' ? 'magiclink' : 'invite',
            });

            if (exchangeError || !data?.session) {
              // If exchange fails, redirect to code entry (user needs to enter code manually)
              navigate('/enter-invite-code');
              return;
            }

            // Success - redirect with tokens
            const sessionParams = new URLSearchParams();
            sessionParams.set('access_token', data.session.access_token);
            sessionParams.set('refresh_token', data.session.refresh_token);
            sessionParams.set('type', typeParam || 'invite');
            window.location.replace(`/draft-invite-auth#${sessionParams.toString()}`);
          } catch {
            navigate('/enter-invite-code');
          }
        };
        exchangeToken();
      }
      return;
    }
  }, [tokenParam, accessToken, typeParam, navigate, sp]);

  // Hooks must be called unconditionally at the top level
  useEffect(() => {
    if (!accessToken || !refreshToken) return;

    // CRITICAL FIX: Check feature toggle before redirecting to mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // Check feature toggle - only redirect if enabled
      import('../../lib/featureFlags').then(({ shouldRedirectToMobile }) => {
        shouldRedirectToMobile().then((shouldRedirect) => {
          if (!shouldRedirect) {
            // Feature toggle disabled - handle web-side, don't redirect
            return;
          }
          
          // Feature toggle enabled - redirect to mobile app
      // Extract company_id and draft_student_id from JWT to pass to mobile app
      const { company_id, draft_student_id } = getJwtPayload(accessToken);

      const params = new URLSearchParams();
      params.set('access_token', accessToken.trim());
      params.set('refresh_token', refreshToken.trim());
      if (tokenType) params.set('token_type', tokenType.trim());
      params.set('type', normalizedType || 'invite');
      // CRITICAL: Pass company_id and draft_student_id to mobile app
      // This ensures the mobile app knows which company the invite is for
      if (company_id) params.set('company_id', company_id);
      if (draft_student_id) params.set('draft_student_id', draft_student_id);

          const deepLink = buildScheme(`/draft-invite-auth?${params.toString()}`);
          
          // Try to open mobile app, but show "Continue on Web" option after a delay
          try { 
            window.location.href = deepLink;
            // Show "Continue on Web" button after 2 seconds if still on page
            setTimeout(() => {
              setShowContinueWeb(true);
            }, 2000);
          } catch { 
            setShowContinueWeb(true);
          }
        });
      });
    }
  }, [accessToken, refreshToken, tokenType, normalizedType]);

  const handleContinueOnWeb = async () => {
    if (!accessToken || !refreshToken) {
      return;
    }

    setProcessing(true);

    try {
      // Set session with the tokens
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (sessionError) {
        throw sessionError;
      }

      // Get user to check role in the invited company only
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Failed to get user');
      }

      // Get company_id and draft_student_id from user_metadata (the company they were invited to)
      // All invites are for students only, so we check role only in this specific company
      const companyId = user.user_metadata?.company_id;
      const draftStudentId = user.user_metadata?.draft_student_id;
      const locationId = user.user_metadata?.location_id;
      
      if (!companyId) {
        // No company_id found - default to success page (all invites are students)
        navigate('/draft-invite-success');
        return;
      }

      // CRITICAL: Ensure user_company_relationship exists for this company
      // The database trigger only fires on user creation/confirmation, not when setting session for existing users
      // So we need to manually ensure the relationship exists
      const { data: existingRelationship } = await supabase
        .from('user_company_relationships')
        .select('id, company_id, system_roles(role)')
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .maybeSingle();

      if (!existingRelationship) {
        // Relationship doesn't exist - create it
        // Get student role ID
        const { data: studentRole } = await supabase
          .from('system_roles')
          .select('system_role_id')
          .eq('role', 'student')
          .single();

        if (!studentRole) {
          navigate('/draft-invite-success');
          return;
        }

        // Create the relationship
        const { error: createRelError } = await supabase
          .from('user_company_relationships')
          .insert({
            user_id: user.id,
            company_id: companyId,
            role_id: studentRole.system_role_id,
            onboarding_complete: false, // Draft invites need onboarding
            primary_location_id: locationId || null,
          });

        if (createRelError) {
          // Continue anyway - might already exist from concurrent request
        }

        // Update draft student record if we have draft_student_id
        if (draftStudentId) {
          const { error: updateDraftError } = await supabase
            .from('staging_import_members')
            .update({
              user_id: user.id,
              invite_accepted_at: new Date().toISOString(),
              status: 'registered',
              updated_at: new Date().toISOString(),
            })
            .eq('id', draftStudentId)
            .neq('status', 'registered'); // Only update if not already registered

          if (updateDraftError) {
            // Non-fatal - continue
          }
        }
      }

      // Refresh session to get updated JWT with company_id claim
      await supabase.auth.refreshSession();

      // Check if user needs onboarding (profile incomplete or EULA not accepted)
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('profile_complete, eula_accepted_at')
        .eq('id', user.id)
        .single();

      const needsEula = !profile?.eula_accepted_at;
      const needsOnboarding = !profile?.profile_complete || needsEula;

      if (needsOnboarding) {
        // Route to student onboarding flow
        navigate('/student-onboarding');
        return;
      }

      // Check user's role ONLY in the invited company (not other companies)
      const { data: relationship, error: relError } = await supabase
        .from('user_company_relationships')
        .select(`
          company_id,
          system_roles (
            role
          )
        `)
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .maybeSingle();

      if (relError) {
        // Default to success page if we can't determine role (all invites are students)
        navigate('/draft-invite-success');
        return;
      }

      // Check role ONLY in the invited company
      // All invites are for students, so they should always route to success page
      // But check just in case they somehow have owner/admin in this specific company
      const role = relationship?.system_roles?.role;
      
      if (role === 'owner' || role === 'admin') {
        // This shouldn't happen for invites, but if it does, route to dashboard
        navigate('/dashboard');
      } else {
        // Student/coach/reception or no role yet - route to success page (all invites are students)
        navigate('/draft-invite-success');
      }
    } catch {
      // On error, still try to navigate to success page
      navigate('/draft-invite-success');
    } finally {
      setProcessing(false);
    }
  };

  // If there's an error, show error page
  if (error || errorCode || errorDescription) {
    const isExpiredError = errorDescription.includes('Error confirming user') ||
                           errorDescription.includes('expired') ||
                           errorDescription.includes('invalid') ||
                           errorCode === 'unexpected_failure' ||
                           error === 'server_error';
    
    return (
      <DeepLinkPage
        title={isExpiredError ? "Invite Link Expired" : "Invitation Error"}
        description={
          isExpiredError
            ? "This invitation link has expired or is no longer valid.\n\nYou can:\n• Request a new invite link from your gym\n• Scan the QR code at the gym to create your account"
            : "There was an issue with your invitation link. Please request a new invite from your gym or scan the QR code at the gym to create your account."
        }
        deepLink={buildScheme('/')}
        showDownloadButtons={false}
      />
    );
  }

  if (!accessToken || !refreshToken) {
    return (
      <DeepLinkPage
        title="Invalid Invitation Link"
        description="We couldn't find a valid invitation. Please request a new email from your gym or scan the QR code at the gym to create your account."
        deepLink={buildScheme('/')}
        showDownloadButtons={false}
      />
    );
  }

  // Build the same deep link params for display
  const displayParams = new URLSearchParams();
  displayParams.set('access_token', accessToken.trim());
  displayParams.set('refresh_token', refreshToken.trim());
  if (tokenType) displayParams.set('token_type', tokenType.trim());
  displayParams.set('type', normalizedType || 'invite');
  // Include company_id and draft_student_id if available from JWT
  const { company_id: jwtCompanyId, draft_student_id: jwtDraftStudentId } = getJwtPayload(accessToken);
  if (jwtCompanyId) displayParams.set('company_id', jwtCompanyId);
  if (jwtDraftStudentId) displayParams.set('draft_student_id', jwtDraftStudentId);
  const deepLink = buildScheme(`/draft-invite-auth?${displayParams.toString()}`);

  return (
    <DeepLinkPage
      title="Processing Invitation"
      description="We are opening the RollOps app to verify your invitation and finish signing you in."
      deepLink={deepLink}
      showDownloadButtons
      extraContent={(
        <div style={{ marginTop: 24 }}>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: showContinueWeb ? 16 : 0 }}>
            Your invitation includes secure tokens and will open directly inside the app. For testing in the simulator, use the commands above.
          </p>
          {showContinueWeb && (
            <Button
              onClick={handleContinueOnWeb}
              disabled={processing}
              variant="outline"
              className="w-full border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent"
            >
              {processing ? 'Processing...' : 'Continue on Web'}
            </Button>
          )}
        </div>
      )}
    />
  );
}
